import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Box,
  Divider,
  Collapse,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import InstagramIcon from "@material-ui/icons/Instagram";
import FacebookIcon from "@material-ui/icons/Facebook";
import ChatIcon from "@material-ui/icons/Chat";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      maxWidth: 540,
      width: "100%",
      maxHeight: "85vh",
    },
  },
  dialogTitle: {
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #e0e0e0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  titleText: {
    fontSize: 18,
    fontWeight: 600,
    color: "#1a1a2e",
  },
  closeButton: {
    padding: 8,
  },
  dialogContent: {
    padding: 0,
  },
  searchContainer: {
    padding: theme.spacing(2, 3, 1.5),
    borderBottom: "1px solid #e0e0e0",
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 1,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  searchField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#f5f7fa",
      "&:hover": { backgroundColor: "#eef1f5" },
      "&.Mui-focused": { backgroundColor: "#fff" },
    },
  },
  filterToggleBtn: {
    borderRadius: 10,
    border: "1.5px solid #e0e0e0",
    padding: "8px 10px",
    minWidth: 0,
    transition: "all 0.2s",
  },
  filterToggleBtnActive: {
    backgroundColor: "#6366f1",
    borderColor: "#6366f1",
    color: "#fff",
    "&:hover": { backgroundColor: "#4f46e5" },
  },
  filtersPanel: {
    padding: theme.spacing(1.5, 3, 2),
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid #e0e0e0",
  },
  filterGroup: {
    marginBottom: theme.spacing(1.5),
    "&:last-child": { marginBottom: 0 },
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 6,
  },
  filterChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  filterChip: {
    height: 26,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    border: "1.5px solid #e0e0e0",
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: "#f3f4f6" },
  },
  filterChipActive: {
    fontWeight: 600,
  },
  clearFiltersBtn: {
    fontSize: 12,
    color: "#6366f1",
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
    display: "inline-block",
    "&:hover": { textDecoration: "underline" },
  },
  resultsList: {
    padding: 0,
    maxHeight: "calc(85vh - 260px)",
    overflowY: "auto",
  },
  listItem: {
    padding: theme.spacing(1.5, 3),
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": { backgroundColor: "#f5f7fa" },
  },
  avatar: {
    width: 48,
    height: 48,
    marginRight: theme.spacing(2),
  },
  contactName: {
    fontWeight: 600,
    fontSize: 15,
    color: "#1a1a2e",
  },
  lastMessage: {
    fontSize: 13,
    color: "#6b7280",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 300,
  },
  ticketInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
    flexWrap: "wrap",
  },
  statusChip: {
    height: 20,
    fontSize: 10,
    fontWeight: 600,
  },
  channelIcon: {
    fontSize: 16,
    marginRight: 4,
  },
  whatsappIcon: { color: "#25d366" },
  instagramIcon: { color: "#e1306c" },
  facebookIcon: { color: "#1877f2" },
  noResults: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: "#9ca3af",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  queueChip: {
    height: 20,
    fontSize: 10,
    fontWeight: 500,
    marginLeft: 4,
  },
  searchHint: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: "#9ca3af",
  },
  searchHintIcon: {
    fontSize: 48,
    color: "#e0e0e0",
    marginBottom: theme.spacing(2),
  },
}));

const STATUS_OPTIONS = [
  { key: "open",    label: "Aberto",    color: "#166534", bg: "#dcfce7" },
  { key: "pending", label: "Pendente",  color: "#92400e", bg: "#fef3c7" },
  { key: "closed",  label: "Resolvido", color: "#6b7280", bg: "#f3f4f6" },
];

const SearchTicketModal = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [searchParam, setSearchParam] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [kanbanTags, setKanbanTags] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);

  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedKanbanTags, setSelectedKanbanTags] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);

  const hasActiveFilters =
    selectedStatus.length > 0 ||
    selectedTags.length > 0 ||
    selectedKanbanTags.length > 0 ||
    selectedWhatsapps.length > 0;

  // Carregar tags e conexões quando o modal abre
  useEffect(() => {
    if (!open) {
      setSearchParam("");
      setTickets([]);
      return;
    }
    api.get("/tags")
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : [];
        setTags(all.filter(t => !t.kanban || t.kanban === 0));
        setKanbanTags(all.filter(t => t.kanban === 1));
      })
      .catch(() => {});
    api.get("/whatsapp", { params: { session: 0 } })
      .then(res => setWhatsapps(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (searchParam.length < 2 && !hasActiveFilters) {
      setTickets([]);
      return;
    }
    const delay = setTimeout(() => fetchTickets(), 500);
    return () => clearTimeout(delay);
  }, [searchParam, selectedStatus, selectedTags, selectedKanbanTags, selectedWhatsapps]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {
        pageNumber: 1,
        showAll: "true",
        searchOnMessages: "true",
      };
      if (searchParam.length >= 2) params.searchParam = searchParam;
      if (selectedWhatsapps.length === 1) params.whatsappId = selectedWhatsapps[0];
      const allSelectedTags = [...selectedTags, ...selectedKanbanTags];
      if (allSelectedTags.length > 0) params["tags[]"] = allSelectedTags;

      const { data } = await api.get("/tickets", { params });
      let ticketsList = data.tickets || [];

      // Filtro de status local (API pode não suportar múltiplos)
      if (selectedStatus.length > 0) {
        ticketsList = ticketsList.filter(t => selectedStatus.includes(t.status));
      }

      // Filtro de múltiplas conexões local
      if (selectedWhatsapps.length > 1) {
        ticketsList = ticketsList.filter(t => selectedWhatsapps.includes(t.whatsappId));
      }

      const search = searchParam.toLowerCase();
      const sorted = ticketsList
        .map(ticket => {
          if (!search || search.length < 2) return { ...ticket, _score: 1 };
          const lastMessage = (ticket.lastMessage || "").toLowerCase();
          const contactName = (ticket.contact?.name || "").toLowerCase();
          const contactNumber = (ticket.contact?.number || "").toLowerCase();
          let score = 0;
          if (lastMessage.includes(search)) score += 10;
          if (contactName.includes(search)) score += 5;
          if (contactNumber.includes(search)) score += 3;
          return { ...ticket, _score: score };
        })
        .filter(t => t._score > 0)
        .sort((a, b) => b._score - a._score);

      setTickets(sorted);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = (key) => {
    setSelectedStatus(prev =>
      prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
    );
  };

  const toggleTag = (id) => {
    setSelectedTags(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const toggleWhatsapp = (id) => {
    setSelectedWhatsapps(prev =>
      prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id]
    );
  };

  const clearFilters = () => {
    setSelectedStatus([]);
    setSelectedTags([]);
    setSelectedKanbanTags([]);
    setSelectedWhatsapps([]);
  };

  const toggleKanbanTag = (id) => {
    setSelectedKanbanTags(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleSelectTicket = (ticket) => {
    onClose();
    history.push(`/atendimentos/${ticket.id}`);
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "whatsapp":
        return <WhatsAppIcon className={`${classes.channelIcon} ${classes.whatsappIcon}`} />;
      case "instagram":
        return <InstagramIcon className={`${classes.channelIcon} ${classes.instagramIcon}`} />;
      case "facebook":
        return <FacebookIcon className={`${classes.channelIcon} ${classes.facebookIcon}`} />;
      default:
        return <ChatIcon className={classes.channelIcon} />;
    }
  };

  const getStatusColor = (status) => {
    const s = STATUS_OPTIONS.find(o => o.key === status);
    return s ? { backgroundColor: s.bg, color: s.color } : { backgroundColor: "#e0e7ff", color: "#3730a3" };
  };

  const getStatusLabel = (status) => {
    const s = STATUS_OPTIONS.find(o => o.key === status);
    return s ? s.label : status;
  };

  const highlightSearchTerm = (text, search) => {
    if (!text || !search || search.length < 2) return text;
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <span key={i} style={{ backgroundColor: "#fef08a", fontWeight: 600 }}>{part}</span>
        : part
    );
  };

  const getMatchingMessagePreview = (ticket) => {
    const message = ticket.lastMessage || "";
    const search = searchParam.toLowerCase();
    if (!message || search.length < 2) {
      return message.length > 60 ? message.substring(0, 60) + "..." : message || "Sem mensagens";
    }
    const lowerMessage = message.toLowerCase();
    const index = lowerMessage.indexOf(search);
    if (index === -1) {
      return message.length > 60 ? message.substring(0, 60) + "..." : message;
    }
    const start = Math.max(0, index - 20);
    const end = Math.min(message.length, index + search.length + 40);
    let preview = message.substring(start, end);
    if (start > 0) preview = "..." + preview;
    if (end < message.length) preview = preview + "...";
    return highlightSearchTerm(preview, searchParam);
  };

  const showResults = searchParam.length >= 2 || hasActiveFilters;

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.titleText}>Buscar Conversas</Typography>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Busca + botão filtros */}
        <div className={classes.searchContainer}>
          <div className={classes.searchRow}>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              placeholder="Digite o nome, número ou mensagem..."
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              size="small"
              className={`${classes.filterToggleBtn} ${(filtersOpen || hasActiveFilters) ? classes.filterToggleBtnActive : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              title="Filtros"
            >
              <FilterListIcon style={{ fontSize: 20 }} />
            </IconButton>
          </div>
        </div>

        {/* Painel de filtros */}
        <Collapse in={filtersOpen}>
          <div className={classes.filtersPanel}>
            {/* Status */}
            <div className={classes.filterGroup}>
              <div className={classes.filterLabel}>Status</div>
              <div className={classes.filterChips}>
                {STATUS_OPTIONS.map(s => (
                  <Chip
                    key={s.key}
                    label={s.label}
                    size="small"
                    onClick={() => toggleStatus(s.key)}
                    className={`${classes.filterChip} ${selectedStatus.includes(s.key) ? classes.filterChipActive : ""}`}
                    style={selectedStatus.includes(s.key)
                      ? { backgroundColor: s.bg, color: s.color, borderColor: s.color }
                      : {}}
                  />
                ))}
              </div>
            </div>

            {/* Etiquetas normais */}
            {tags.length > 0 && (
              <div className={classes.filterGroup}>
                <div className={classes.filterLabel}>Etiquetas</div>
                <div className={classes.filterChips}>
                  {tags.map(tag => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      onClick={() => toggleTag(tag.id)}
                      className={`${classes.filterChip} ${selectedTags.includes(tag.id) ? classes.filterChipActive : ""}`}
                      style={selectedTags.includes(tag.id)
                        ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                        : { borderColor: tag.color || "#e0e0e0" }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Etapas Kanban */}
            {kanbanTags.length > 0 && (
              <div className={classes.filterGroup}>
                <div className={classes.filterLabel}>Kanban</div>
                <div className={classes.filterChips}>
                  {kanbanTags.map(tag => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      onClick={() => toggleKanbanTag(tag.id)}
                      className={`${classes.filterChip} ${selectedKanbanTags.includes(tag.id) ? classes.filterChipActive : ""}`}
                      style={selectedKanbanTags.includes(tag.id)
                        ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                        : { borderColor: tag.color || "#e0e0e0" }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Conexões */}
            {whatsapps.length > 0 && (
              <div className={classes.filterGroup}>
                <div className={classes.filterLabel}>Conexões</div>
                <div className={classes.filterChips}>
                  {whatsapps.map(w => (
                    <Chip
                      key={w.id}
                      label={w.name}
                      size="small"
                      onClick={() => toggleWhatsapp(w.id)}
                      className={`${classes.filterChip} ${selectedWhatsapps.includes(w.id) ? classes.filterChipActive : ""}`}
                      style={selectedWhatsapps.includes(w.id)
                        ? { backgroundColor: "#25d366", color: "#fff", borderColor: "#25d366" }
                        : { borderColor: "#25d366" }}
                    />
                  ))}
                </div>
              </div>
            )}

            {hasActiveFilters && (
              <span className={classes.clearFiltersBtn} onClick={clearFilters}>
                Limpar filtros
              </span>
            )}
          </div>
        </Collapse>

        {/* Resultados */}
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={32} />
          </div>
        ) : !showResults ? (
          <div className={classes.searchHint}>
            <SearchIcon className={classes.searchHintIcon} />
            <Typography>
              Digite pelo menos 2 caracteres para buscar
            </Typography>
            {!filtersOpen && (
              <Typography style={{ fontSize: 12, color: "#b0b0b0", marginTop: 6 }}>
                Use o botão <FilterListIcon style={{ fontSize: 14, verticalAlign: "middle" }} /> para filtrar por status, etiquetas ou conexões
              </Typography>
            )}
          </div>
        ) : tickets.length === 0 ? (
          <div className={classes.noResults}>
            <Typography>Nenhuma conversa encontrada</Typography>
          </div>
        ) : (
          <List className={classes.resultsList}>
            {tickets.map((ticket, index) => (
              <React.Fragment key={ticket.id}>
                <ListItem
                  className={classes.listItem}
                  onClick={() => handleSelectTicket(ticket)}
                >
                  <ListItemAvatar>
                    <Avatar className={classes.avatar} src={ticket.contact?.profilePicUrl}>
                      {ticket.contact?.name?.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography className={classes.contactName}>
                        {ticket.contact?.name || "Sem nome"}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography className={classes.lastMessage} component="div">
                          {getMatchingMessagePreview(ticket)}
                        </Typography>
                        <div className={classes.ticketInfo}>
                          {getChannelIcon(ticket.channel)}
                          <Chip
                            label={getStatusLabel(ticket.status)}
                            size="small"
                            className={classes.statusChip}
                            style={getStatusColor(ticket.status)}
                          />
                          {ticket.queue && (
                            <Chip
                              label={ticket.queue.name}
                              size="small"
                              className={classes.queueChip}
                              style={{
                                backgroundColor: (ticket.queue.color || "#6366f1") + "20",
                                color: ticket.queue.color || "#6366f1",
                              }}
                            />
                          )}
                          <Typography variant="caption" style={{ color: "#9ca3af", marginLeft: "auto" }}>
                            #{ticket.id}
                          </Typography>
                        </div>
                      </Box>
                    }
                  />
                </ListItem>
                {index < tickets.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchTicketModal;
